import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../servicios/api.js';
import { Aviso, CasillaVerificacion, mensajeError } from './piezas.js';

/** Forma comparable de un nombre: sin tildes, sin mayúsculas, sin signos ni espacios. */
export function formaComparable(nombre: string): string {
  return nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function distanciaEdicion(a: string, b: string): number {
  const previa = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let diagonal = previa[0]!;
    previa[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const temporal = previa[j]!;
      previa[j] = Math.min(previa[j]! + 1, previa[j - 1]! + 1, diagonal + (a[i - 1] === b[j - 1] ? 0 : 1));
      diagonal = temporal;
    }
  }
  return previa[b.length]!;
}

/** Nombres que se parecen al escrito: iguales, uno contiene al otro o difieren en muy pocas letras. */
export function fabricantesParecidos(nombre: string, existentes: { id: string; nombre: string }[]) {
  const buscado = formaComparable(nombre);
  if (buscado.length < 3) return [];
  return existentes.filter((f) => {
    const forma = formaComparable(f.nombre);
    if (forma === buscado) return true;
    if (forma.length >= 3 && (forma.includes(buscado) || buscado.includes(forma))) return true;
    return distanciaEdicion(forma, buscado) <= Math.max(1, Math.floor(buscado.length / 5));
  });
}

/**
 * Alta de un fabricante desde el formulario de activo. Si hay nombres parecidos, pide
 * confirmar que es otro fabricante. Si el nombre es identico a uno existente, no deja crearlo.
 */
export function NuevoFabricante({
  existentes,
  alCrear,
  alCerrar,
}: {
  existentes: { id: string; nombre: string }[];
  alCrear: (id: string) => void;
  alCerrar: () => void;
}): JSX.Element {
  const cliente = useQueryClient();
  const [nombre, setNombre] = useState('');
  const [confirmado, setConfirmado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nombreLimpio = nombre.trim();
  const parecidos = fabricantesParecidos(nombreLimpio, existentes);
  const identico = parecidos.find((f) => formaComparable(f.nombre) === formaComparable(nombreLimpio));
  const necesitaConfirmacion = parecidos.length > 0 && !identico;
  const puedeCrear = nombreLimpio.length > 0 && !identico && (!necesitaConfirmacion || confirmado);

  const crear = useMutation({
    mutationFn: () => api.post<{ id: string }>('/catalogos', { catalogo: 'FABRICANTE', nombre: nombreLimpio }),
    onSuccess: (creado) => {
      void cliente.invalidateQueries({ queryKey: ['catalogo', 'FABRICANTE'] });
      alCrear(creado.id);
    },
    onError: (e) => setError(mensajeError(e, 'No se pudo crear el fabricante.')),
  });

  return (
    <div className="panel ancho-completo" style={{ marginTop: 8 }}>
      <div className="panel-cabecera">
        <h2>Nuevo fabricante</h2>
        <button type="button" className="boton secundario pequeno" onClick={alCerrar}>
          Cancelar
        </button>
      </div>
      <div className="panel-cuerpo" style={{ display: 'grid', gap: 10 }}>
        <div className="campo">
          <label htmlFor="nuevo-fabricante-nombre">Nombre del fabricante</label>
          <input
            id="nuevo-fabricante-nombre"
            value={nombre}
            onChange={(e) => {
              setNombre(e.target.value);
              setConfirmado(false);
              setError(null);
            }}
            maxLength={150}
            placeholder="Ej.: Depilase"
          />
        </div>

        {identico ? (
          <Aviso tono="atencion">
            Ya existe el fabricante «{identico.nombre}». Ciérrelo y elíjalo de la lista.
          </Aviso>
        ) : null}

        {necesitaConfirmacion ? (
          <div className="campo">
            <Aviso tono="atencion">
              Hay fabricantes con nombre parecido: {parecidos.map((f) => `«${f.nombre}»`).join(', ')}. Si es el mismo,
              elíjalo de la lista en vez de crearlo.
            </Aviso>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <CasillaVerificacion
                marcada={confirmado}
                alCambiar={() => setConfirmado((v) => !v)}
                etiqueta="Confirmo que es un fabricante distinto"
              />
              <span>Confirmo que es un fabricante distinto</span>
            </div>
          </div>
        ) : null}

        {error ? <Aviso tono="atencion">{error}</Aviso> : null}

        <div>
          <button
            type="button"
            className="boton pequeno"
            disabled={!puedeCrear || crear.isPending}
            onClick={() => crear.mutate()}
          >
            Crear fabricante
          </button>
        </div>
      </div>
    </div>
  );
}
